// MACHINERY V2 (Stroje) — ChromeNavV4 right-cluster user menu (M2.1).
//
// Reference impl: design_handoff_machinery_v3/machinery-chrome-nav.jsx
// (UserMenu — the `.ms-menu` family).
//
// SPLIT AT M2.2 (this file was 291 lines, inside the 300-line cap's tripwire,
// and M2.2 had to add a palette section to the user menu). The two pieces that
// moved out:
//   - chromeMenuContent.tsx — MenuContent, the CANONICAL portal-rule wrapper.
//   - ChromeLangMenu.tsx    — the language menu.
// `ChromeLangMenu` is re-exported below so ChromeNavV4's existing import
// (`import { ChromeLangMenu, ChromeUserMenu } from "./ChromeRightMenus"`) keeps
// working and the split stays invisible to consumers.
//
// Behaviour is the live app's, unchanged: the logout sequence mirrors
// Header.tsx exactly (accountContext.logout -> refreshAccount(null) -> sign-in
// with return URL). That sequence moved to useChromeActions.ts at M2.16a —
// VERBATIM — because the phone drawer offers the same log-out row, and a
// half-copied session teardown is the worst kind of duplicate. The IDENTITY
// derivation (initial, role · company) followed it at the fix pass, for the
// reason written at `useChromeIdentity`: the M2.12 obligation it carries must
// have exactly one place to be discharged.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Modules } from "../../../../core/models/PermissionModels";
import { setPalette } from "../machineryV2UiSlice";
import { NgExit, NgGear } from "./ChromeNavIcons";
import { MenuContent } from "./chromeMenuContent";
import { canSeeModule } from "./navModules";
import { PALETTE_OPTIONS, useChromeIdentity, useChromeLogout } from "./useChromeActions";

export { default as ChromeLangMenu } from "./ChromeLangMenu";

/** INTERIM, PENDING M2.12 REVIEW. M2.2 deleted MachineryV2FoundationShell (its
 *  delete-at-M2.2 marker), which was the ONLY dispatcher of setPalette in the
 *  app — so the owner could no longer A/B the two palettes on `.cc`, even though
 *  `palette` is still persisted and still drives the root class. This section
 *  restores that control in the one place a v2 page always has: the user menu.
 *
 *  >>> AND THIS MENU IS NOT MOUNTED BELOW 768. <<< MobileDrawer's account
 *  section offers the same two options for exactly that reason — M2.16a
 *  replaced ChromeNavV4 wholesale on phone and took the only palette dispatcher
 *  with it, locking a phone user into whatever a desktop session last chose.
 *  The OPTION TABLE is therefore shared (useChromeActions.PALETTE_OPTIONS), not
 *  copied. At M2.12 the nav is promoted app-wide and this must be re-decided —
 *  either the palette becomes a real Settings preference or it retires with the
 *  exploration, and that call now covers BOTH surfaces. Do not build more UI on
 *  top of it before then.
 *
 *  Eyebrow + the two options. Radix keeps focus/keyboard handling; the check
 *  mark is the only "selected" affordance so the row height never shifts. */
const PaletteSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation(["myMachinery", "common"]);
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);

  return (
    <>
      <div style={{ height: 1, background: "var(--border-soft)", margin: "4px 6px" }} />
      <div
        className="eyebrow"
        style={{ padding: "4px 12px 3px", pointerEvents: "none" }}
      >
        {translate("myMachinery:v2.palette.label")}
      </div>
      {PALETTE_OPTIONS.map((p) => (
        <DropdownMenu.Item
          key={p.id}
          className="mi"
          // Radix closes the menu on select; keeping it open would need
          // preventDefault plus its own dismissal story for one dev affordance.
          onSelect={() => dispatch(setPalette(p.id))}
          style={palette === p.id ? { color: "var(--mod-accent)", fontWeight: 600 } : undefined}
        >
          <span className="mi-ico">{palette === p.id ? "✓" : ""}</span>
          {translate(p.labelKey)}
        </DropdownMenu.Item>
      ))}
    </>
  );
};

export interface ChromeUserMenuProps {
  /** Active module accent — the avatar adopts it (artboard rule). */
  accent: string;
}

/** Name + avatar trigger, user menu (identity header, Settings, Logout). */
export const ChromeUserMenu: React.FC<ChromeUserMenuProps> = ({ accent }) => {
  const { translate } = useTranslation(["layout", "common"]);
  const navigate = useNavigate();
  const logout = useChromeLogout();
  // The SAME derivation the phone drawer renders — see useChromeIdentity for
  // the M2.12 obligation that is the reason it is not four lines inline.
  const { user, initial, subLine } = useChromeIdentity();

  if (!user) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            border: 0,
            background: "transparent",
            padding: 0,
            cursor: "pointer",
          }}
        >
          <span
            style={{
              fontSize: 12.5,
              color: "var(--ink-2)",
              whiteSpace: "nowrap",
              maxWidth: 110,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user.firstName}
          </span>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              background: accent,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontSize: 11.5,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initial}
          </span>
        </button>
      </DropdownMenu.Trigger>
      <MenuContent width={208}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 12px 9px" }}>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 999,
              background: accent,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initial}
          </span>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ink)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user.firstName}
            </div>
            {subLine && (
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 9.5,
                  color: "var(--faint)",
                  letterSpacing: "0.04em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subLine}
              </div>
            )}
          </div>
        </div>
        <div style={{ height: 1, background: "var(--border-soft)", margin: "0 6px 4px" }} />
        <PaletteSection />
        <div style={{ height: 1, background: "var(--border-soft)", margin: "4px 6px" }} />
        {/* /settings sits behind PermissionRouteGuard(Settings): an ungated
            item would navigate a Settings:None user straight into a denial.
            Mirrors the legacy user menu (Header.tsx:1209). Logout below needs
            no gate, and the identity header is data the user already owns —
            those are the only other items in this menu. */}
        {canSeeModule(Modules.Settings) && (
          <DropdownMenu.Item className="mi" onSelect={() => navigate("/settings")}>
            <span className="mi-ico">
              <NgGear />
            </span>
            {translate("common:navigation.settings")}
          </DropdownMenu.Item>
        )}
        <DropdownMenu.Item className="mi" onSelect={logout}>
          <span className="mi-ico">
            <NgExit />
          </span>
          {translate("common:navigation.logout")}
        </DropdownMenu.Item>
      </MenuContent>
    </DropdownMenu.Root>
  );
};
